import React from 'react';
import { Box, keyframes } from '@mui/material';

// Animation keyframes
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(102, 126, 234, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const AnimatedLogo = ({ 
  width = 80, 
  height = 80, 
  animate = true,
  showStroke = true,
  animationType = 'pulse' // 'pulse', 'float', 'rotate', 'none'
}) => {
  const getAnimation = () => {
    if (!animate) return 'none';
    
    switch (animationType) {
      case 'pulse':
        return `${pulseGlow} 2s infinite`;
      case 'float':
        return `${float} 3s ease-in-out infinite`;
      case 'rotate':
        return `${rotate} 8s linear infinite`;
      case 'combined':
        return `${pulseGlow} 2s infinite, ${float} 3s ease-in-out infinite`;
      default:
        return `${pulseGlow} 2s infinite`;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Animated Container */}
      <Box
        sx={{
          width: width,
          height: height,
          borderRadius: '50%',
          padding: '4px',
          background: showStroke 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
            : 'transparent',
          animation: getAnimation(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          // Add a subtle inner shadow for depth
          '&::before': showStroke ? {
            content: '""',
            position: 'absolute',
            top: 2,
            left: 2,
            right: 2,
            bottom: 2,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            pointerEvents: 'none',
          } : {},
        }}
      >
        {/* Inner Container for Logo */}
        <Box
          sx={{
            width: showStroke ? width - 8 : width,
            height: showStroke ? height - 8 : height,
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            // Subtle shadow for the logo itself
            boxShadow: showStroke 
              ? '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.05)' 
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Logo Image */}
          <img
            src="/icons/hyrex-android-chrome-192x192.png"
            alt="HyreX Logo"
            style={{
              width: showStroke ? width - 16 : width - 8,
              height: showStroke ? height - 16 : height - 8,
              objectFit: 'contain',
              borderRadius: '50%',
            }}
          />
        </Box>

        {/* Optional animated background elements */}
        {animate && animationType === 'combined' && (
          <>
            {/* Rotating ring */}
            <Box
              sx={{
                position: 'absolute',
                width: width + 16,
                height: height + 16,
                border: '2px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '50%',
                borderTopColor: 'rgba(102, 126, 234, 0.5)',
                animation: `${rotate} 4s linear infinite`,
                pointerEvents: 'none',
              }}
            />
            {/* Secondary ring */}
            <Box
              sx={{
                position: 'absolute',
                width: width + 24,
                height: height + 24,
                border: '1px solid rgba(118, 75, 162, 0.1)',
                borderRadius: '50%',
                borderBottomColor: 'rgba(118, 75, 162, 0.3)',
                animation: `${rotate} 6s linear infinite reverse`,
                pointerEvents: 'none',
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default AnimatedLogo;
